/*
 * Media URL + filename helpers. Same-origin storage, no CORS.
 * In local mode, paths can be idb:// — components use useMediaUrl() which
 * resolves the Blob to an object URL on first use.
 *
 * See IMPLEMENTATION_PLAN.md §5 "Image storage (Hostinger-aware)".
 */

import { useEffect, useState } from 'react';
import type { Scene, SceneImage, SceneVideo } from '@/types';
import { isLocalMode } from '@/local';
import { resolveBlobUrl } from '@/local/localBlobs';
import { apiUrl } from './apiBase';

export function mediaUrl(episodeUuid: string, relativePath: string): string {
  if (relativePath.startsWith('idb://')) {
    // Caller must use useMediaUrl() in components — synchronous mediaUrl()
    // only works for HTTP-served paths.
    return relativePath;
  }
  return apiUrl(`/api/episode/${encodeURIComponent(episodeUuid)}/media/${relativePath}`);
}

export function useMediaUrl(episodeUuid: string, relativePath: string): string {
  const [resolved, setResolved] = useState(() =>
    relativePath.startsWith('idb://') ? '' : mediaUrl(episodeUuid, relativePath),
  );

  useEffect(() => {
    let cancelled = false;
    if (!relativePath.startsWith('idb://')) {
      setResolved(mediaUrl(episodeUuid, relativePath));
      return;
    }
    void resolveBlobUrl(relativePath).then((url) => {
      if (!cancelled) setResolved(url ?? '');
    });
    return () => {
      cancelled = true;
    };
  }, [episodeUuid, relativePath]);

  return resolved;
}

export function downloadFilename(
  episodeSlug: string,
  scene: Scene,
  asset: SceneImage | SceneVideo,
  variantIndex: number,
): string {
  const ext = asset.path.split('.').pop()?.split('?')[0] ?? (isLocalMode() ? 'png' : 'png');
  const sceneNumber = String(scene.order).padStart(2, '0');
  return `merjem-${slugify(episodeSlug)}-scene-${sceneNumber}-v${variantIndex + 1}.${ext}`;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
